"use client"

import { usePathname } from "next/navigation"
import { Navbar } from "./Navbar"
import { Footer } from "./Footer"
import { cn } from "@/lib/utils"

interface SiteShellProps {
    children: React.ReactNode
}

export function SiteShell({ children }: SiteShellProps) {
    const pathname = usePathname()

    // Check if the current route is an admin route or isolated standalone app (e.g. /puntozero)
    const isAdminRoute = pathname?.startsWith("/admin")
    const isPuntoZeroRoute = pathname?.startsWith("/puntozero")

    // If it's an isolated route, we return children without DragonL2's Navbar, Footer, or gaming backgrounds
    if (isAdminRoute || isPuntoZeroRoute) {
        return <>{children}</>
    }

    // Standard site shell for all other routes
    const isHomePage = pathname === "/"
    const isDashboardRoute = pathname?.startsWith("/dashboard")

    return (
        <div className="relative min-h-screen flex flex-col bg-[#0B0908] text-[#EDE3D4] overflow-x-hidden">
            {/* Background for public section (Option 3 Dragon Crest Emblem + dark firey overlays) */}
            {!isDashboardRoute && (
                <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
                    <div
                        className="absolute inset-0 bg-[url('/images/dragon_crest_emblem.jpg')] bg-contain md:bg-[length:850px_850px] xl:bg-[length:1050px_1050px] bg-center bg-no-repeat opacity-40 transition-opacity duration-700"
                    />
                    {/* Dark fiery volcanic ambient aura */}
                    <div className="absolute top-[30%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-b from-[#E05A47]/15 via-[#681812]/20 to-transparent rounded-full blur-[130px]" />
                    <div className="absolute inset-0 bg-gradient-to-b from-[#0B0908]/40 via-[#0B0908]/75 to-[#0B0908]/98" />
                    <div className="absolute inset-0 [background:radial-gradient(ellipse_at_center,transparent_30%,#0B0908_85%)]" />
                </div>
            )}

            {/* Background for user panel / dashboard section */}
            {isDashboardRoute && (
                <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
                    <div
                        className="absolute inset-0 bg-[url('/images/user_panel_bg.jpg')] bg-cover bg-center bg-no-repeat opacity-30 transition-opacity duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-[#0B0908]/70 via-[#0B0908]/85 to-[#0B0908]/98" />
                </div>
            )}

            <Navbar />
            <main className={cn("relative z-10 flex-1", !isHomePage && "pt-24 md:pt-32")}>
                {children}
            </main>
            <Footer />
        </div>
    )
}
